import { createPortal } from "react-dom";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

const PANEL_COPY = (
  <>
    <p className="workspace-knowledge-info-title">How NEXUS uses workspace knowledge</p>
    <ol className="workspace-knowledge-info-list">
      <li>
        When you save, your brief and themes are indexed for this workspace only (other tenants never
        see them).
      </li>
      <li>
        On the dashboard, Engagement, and Portfolio, NEXUS uses that context for tone and priorities. Live
        numbers still come from analytics, not from this form.
      </li>
      <li>
        Replies can list Sources such as &quot;Workspace brief (Studio)&quot; when your content was used.
      </li>
    </ol>
    <p className="workspace-knowledge-info-foot">
      Organization context and language only — not scores or live metrics.
    </p>
  </>
);

/** Help control for Studio → Knowledge (opens a fixed panel via portal, not an in-flow tooltip). */
export default function WorkspaceKnowledgeInfoButton({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const titleId = useId();

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const panelW = Math.min(352, window.innerWidth - 24);
    let left = rect.left;
    if (left + panelW > window.innerWidth - 12) {
      left = window.innerWidth - panelW - 12;
    }
    left = Math.max(12, left);
    let top = rect.bottom + 8;
    const maxTop = window.innerHeight - 280;
    if (top > maxTop) {
      top = Math.max(12, rect.top - 8 - 240);
    }
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointerDown = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const panel =
    open &&
    createPortal(
      <div
        ref={panelRef}
        role="dialog"
        aria-labelledby={titleId}
        className="workspace-knowledge-info-panel"
        style={{ top: pos.top, left: pos.left }}
      >
        <div id={titleId} className="sr-only">
          How NEXUS uses workspace knowledge
        </div>
        {PANEL_COPY}
        <button type="button" className="workspace-knowledge-info-close" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>,
      document.body
    );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`workspace-knowledge-info-btn ${className}`.trim()}
        aria-label="How NEXUS uses workspace knowledge"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        i
      </button>
      {panel}
    </>
  );
}
